<template>
  <section v-if="states.length" class="c2c-resume-banner">
    <div class="c2c-resume-head">
      <AlertTriangle :size="20" />
      <strong>{{ t('resume.heading', { n: states.length }) }}</strong>
    </div>
    <p class="c2c-resume-desc">{{ t('resume.desc') }}</p>

    <ul class="c2c-resume-list">
      <li v-for="state in states" :key="state.id" class="c2c-resume-item">
        <div class="c2c-resume-info">
          <strong class="c2c-resume-name">{{ state.fileName }}</strong>
          <span class="c2c-resume-meta">
            {{ t('resume.percent_done', {
              percent: percentDone(state),
              uploaded: formatBytes(state.offset),
              total: formatBytes(state.totalBytes)
            }) }}
            <span class="c2c-resume-age"> · {{ t('resume.started_ago', { ago: timeAgo(state.createdAt) }) }}</span>
          </span>
        </div>
        <div class="c2c-resume-actions">
          <input
            type="file"
            :ref="el => setFileInput(state.id, el as HTMLInputElement | null)"
            class="c2c-resume-file-input"
            accept="video/*,.mp4,.mov,.mkv,.webm,.m4v"
            @change="onFileChosen(state, $event)"
          />
          <!--
            When the encoded output is still on disk there is nothing to
            pick: the upload carries on with the exact bytes Commons already
            holds part of. Asking for the source file in that case was both
            unnecessary and, until the size comparison was fixed, impossible
            to satisfy.
          -->
          <CdxButton
            action="progressive"
            weight="primary"
            size="small"
            @click="onResumeClick(state)"
          >{{ resumable.has(state.id)
            ? t('resume.resume_button')
            : t('resume.resume_button_pick') }}</CdxButton>
          <CdxButton
            action="destructive"
            size="small"
            @click="discard(state)"
          >{{ t('resume.discard_button') }}</CdxButton>
        </div>
      </li>
    </ul>
  </section>

  <AppDialog
    :visible="!!mismatchDialog"
    :title="t('resume.dialog_mismatch_title')"
    :message="mismatchDialog || ''"
    tone="warn"
    :confirm-label="t('common.ok')"
    @confirm="mismatchDialog = null"
    @cancel="mismatchDialog = null"
  />

  <AppDialog
    :visible="!!pendingDiscard"
    :title="t('resume.dialog_discard_title')"
    :message="pendingDiscard ? t('resume.confirm_discard', { name: pendingDiscard.fileName }) : ''"
    tone="warn"
    :confirm-label="t('resume.confirm_discard_yes')"
    :cancel-label="t('common.cancel')"
    @confirm="performDiscard"
    @cancel="pendingDiscard = null"
  />
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue"
import { AlertTriangle } from "lucide-vue-next"
import { useI18n } from "vue-i18n"
import { CdxButton } from "@wikimedia/codex"
import { listResumeStates, clearResumeState } from "@/lib/resume-storage"
import { openEncodedFile } from "@/lib/encoded-store"
import type { ResumeState } from "@/lib/types"
import AppDialog from "@/components/AppDialog.vue"

const { t } = useI18n()

const emit = defineEmits<{
  resume: [state: ResumeState, file: File]
  "resume-stored": [ResumeState]
}>()

const states = ref<ResumeState[]>([])
const fileInputs = new Map<string, HTMLInputElement | null>()
const mismatchDialog = ref<string | null>(null)
const pendingDiscard = ref<ResumeState | null>(null)

function setFileInput(id: string, el: HTMLInputElement | null) {
  fileInputs.set(id, el)
}

function triggerFilePick(id: string) {
  const el = fileInputs.get(id)
  el?.click()
}

async function refresh() {
  states.value = await listResumeStates()
  await refreshResumable(states.value)
}

onMounted(refresh)

defineExpose({ refresh })

/**
 * Which entries still have their encoded output on disk, and so can carry on
 * without the contributor finding the original file again.
 */
const resumable = ref<Set<string>>(new Set())

async function refreshResumable(list: ResumeState[]) {
  const usable = new Set<string>()
  for (const s of list) {
    if (s.outputKey && await openEncodedFile(s.outputKey)) usable.add(s.id)
  }
  resumable.value = usable
}

function onResumeClick(state: ResumeState) {
  if (resumable.value.has(state.id)) {
    emit("resume-stored", state)
    return
  }
  triggerFilePick(state.id)
}

function onFileChosen(state: ResumeState, e: Event) {
  const input = e.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ""
  if (!file) return

  // Match against the SOURCE the contributor picked, not the upload
  // payload. These differ for anything that was re-encoded - a 557 MB
  // recording becomes a 147 MB WebM - so comparing the chosen file against
  // fileSize reported the right file as the wrong one, and cross-session
  // resume could never succeed for a re-encoded upload at all. Entries
  // written before sourceSize existed fall back to the old field, which was
  // only ever correct for passthroughs anyway.
  const expectedSize = state.sourceSize ?? state.fileSize
  if (file.size !== expectedSize) {
    mismatchDialog.value = t('resume.alert_mismatch', {
      expected: formatBytes(expectedSize),
      got: formatBytes(file.size)
    })
    return
  }

  emit("resume", state, file)
}

async function discard(state: ResumeState) {
  pendingDiscard.value = state
}

async function performDiscard() {
  const s = pendingDiscard.value
  pendingDiscard.value = null
  if (!s) return
  await clearResumeState(s.id)
  await refresh()
}

function percentDone(state: ResumeState): number {
  if (!state.totalBytes) return 0
  return Math.round((state.offset / state.totalBytes) * 100)
}

function formatBytes(bytes: number): string {
  if (!bytes || bytes < 0) return "0 B"
  if (bytes < 1024) return bytes + " B"
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB"
  if (bytes < 1024 * 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(2) + " MB"
  return (bytes / (1024 * 1024 * 1024)).toFixed(2) + " GB"
}

function timeAgo(iso: string): string {
  const ms = Date.now() - Date.parse(iso)
  if (!Number.isFinite(ms) || ms < 0) return ""
  const m = Math.floor(ms / 60000)
  if (m < 1) return t('resume.ago_just_now')
  if (m < 60) return t('resume.ago_minutes', { n: m })
  const h = Math.floor(m / 60)
  return t('resume.ago_hours', { n: h })
}
</script>

<style scoped>
.c2c-resume-banner {
  margin-bottom: 16px; padding: 16px;
  background: #fef6e7; border: 1px solid #f0d795; border-radius: 6px;
}
.c2c-resume-head {
  display: flex; align-items: center; gap: 8px;
  color: #886600; margin-bottom: 4px;
}
.c2c-resume-head strong { font-size: 14px; }
.c2c-resume-desc {
  margin: 0 0 12px 0; font-size: 13px; color: #54595d;
}
.c2c-resume-list { list-style: none; padding: 0; margin: 0; }
.c2c-resume-item {
  display: flex; justify-content: space-between; align-items: center;
  background: white; border: 1px solid #eaecf0; border-radius: 4px;
  padding: 10px 12px; margin-bottom: 8px; gap: 12px;
}
.c2c-resume-item:last-child { margin-bottom: 0; }
.c2c-resume-info {
  display: flex; flex-direction: column; gap: 2px; min-width: 0; flex: 1 1 auto;
}
.c2c-resume-name { font-size: 13px; word-break: break-word; }
.c2c-resume-meta { font-size: 11px; color: #54595d; }
.c2c-resume-age { color: #72777d; }
.c2c-resume-actions { display: flex; gap: 6px; flex-shrink: 0; }
.c2c-resume-file-input { display: none; }
@media (max-width: 540px) {
  .c2c-resume-item { flex-direction: column; align-items: stretch; }
  .c2c-resume-actions { justify-content: flex-end; }
}
</style>
